// frontend/src/pages/TutorFeedbackDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Select, Input, Tag, Button, Modal, Row, Col, Statistic, Progress, Space, Typography, Badge, Tooltip } from 'antd';
import { MessageOutlined, CheckCircleOutlined, ExclamationCircleOutlined, SearchOutlined, UserOutlined, EyeOutlined, ArrowLeftOutlined, CalendarOutlined } from '@ant-design/icons';
import { getFeedbackStatus } from '../services/feedbackService';
import { getAllClasses } from '../services/classService';
import { useNotification } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import useResponsive from '../hooks/useResponsive';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const TutorFeedbackDashboard = () => {
    const navigate = useNavigate();
    const { isMobile } = useResponsive();
    const { message: notify } = useNotification();

    const [term, setTerm] = useState('Term 1');
    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);

    // Filters
    const [searchText, setSearchText] = useState('');
    const [selectedClass, setSelectedClass] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const classData = await getAllClasses();
                setClasses(classData);
            } catch (error) {
                console.error('Error fetching classes:', error);
            }
        };
        fetchClasses();
    }, []);

    useEffect(() => {
        const fetchStatus = async () => {
            setLoading(true);
            try {
                const data = await getFeedbackStatus(term);
                setStudents(data);
            } catch (error) {
                console.error('Error fetching feedback status:', error);
                notify.error('Failed to load feedback status.');
            } finally {
                setLoading(false);
            }
        };
        fetchStatus();
    }, [term, notify]);

    // Truncate feedback helper
    const truncateText = (text, maxLength = 60) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return `${text.substring(0, maxLength)}...`;
    };

    // Calculate statistics
    const stats = useMemo(() => {
        const total = students.length;
        const submitted = students.filter(s => s.status === 'submitted').length;
        const pending = total - submitted;
        const percentage = total > 0 ? Math.round((submitted / total) * 100) : 0;
        return { total, submitted, pending, percentage };
    }, [students]);

    // Filter students
    const filteredStudents = useMemo(() => {
        return students.filter(student => {
            const matchesSearch = 
                student.student_name.toLowerCase().includes(searchText.toLowerCase()) ||
                student.admission_no.toLowerCase().includes(searchText.toLowerCase()) ||
                (student.tutor_name && student.tutor_name.toLowerCase().includes(searchText.toLowerCase()));
            
            const matchesClass = selectedClass === 'All' || student.class_name === selectedClass;
            const matchesStatus = statusFilter === 'All' || student.status === statusFilter;

            return matchesSearch && matchesClass && matchesStatus;
        });
    }, [students, searchText, selectedClass, statusFilter]);

    const handleViewFeedback = (record) => {
        setSelectedFeedback(record);
        setModalVisible(true);
    };

    const columns = [
        {
            title: 'Admission No',
            dataIndex: 'admission_no',
            key: 'admission_no',
            width: 120,
            sorter: (a, b) => a.admission_no.localeCompare(b.admission_no),
        },
        {
            title: 'Student Name',
            dataIndex: 'student_name',
            key: 'student_name',
            fontWeight: 'bold',
            sorter: (a, b) => a.student_name.localeCompare(b.student_name),
            render: (text, record) => (
                <Button 
                    type="link" 
                    onClick={() => navigate(`/students/${record.student_id}`)} 
                    style={{ padding: 0, fontWeight: 600, height: 'auto', textAlign: 'left' }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: 'Class',
            dataIndex: 'class_name',
            key: 'class_name',
            width: 100,
            sorter: (a, b) => a.class_name.localeCompare(b.class_name),
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Assigned Tutor',
            dataIndex: 'tutor_name',
            key: 'tutor_name',
            render: (text) => text ? (
                <Space>
                    <UserOutlined style={{ color: '#8c8c8c' }} />
                    <Text strong>{text}</Text>
                </Space>
            ) : (
                <Text type="secondary" italic>No tutor assigned</Text>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            align: 'center',
            sorter: (a, b) => a.status.localeCompare(b.status),
            render: (status) => (
                status === 'submitted' ? (
                    <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: '4px', padding: '2px 8px' }}>
                        SUBMITTED
                    </Tag>
                ) : (
                    <Tag color="warning" icon={<ExclamationCircleOutlined />} style={{ borderRadius: '4px', padding: '2px 8px' }}>
                        PENDING
                    </Tag>
                )
            )
        },
        {
            title: 'Feedback Response Preview',
            dataIndex: 'feedback',
            key: 'feedback',
            ellipsis: true,
            render: (text) => <Text type={text ? 'normal' : 'secondary'}>{truncateText(text)}</Text>
        },
        {
            title: 'Submission Date',
            dataIndex: 'feedback_date',
            key: 'feedback_date',
            width: 140,
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Tooltip title={record.status === 'submitted' ? 'View feedback text' : 'No feedback submitted'}>
                    <Button 
                        type="text" 
                        icon={<EyeOutlined />} 
                        disabled={record.status !== 'submitted'}
                        onClick={() => handleViewFeedback(record)}
                    />
                </Tooltip>
            )
        }
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '32px', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                    <div>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')} style={{ marginBottom: 8 }} />
                        <Title level={2} style={{ fontWeight: 800, margin: 0 }}>Tutor Feedback Monitoring</Title>
                        <Paragraph type="secondary" style={{ fontSize: 15, margin: 0 }}>
                            Review student assessments and track feedback submission progress across classes.
                        </Paragraph>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Text strong style={{ fontSize: 16 }}>Select Term:</Text>
                        <Select 
                            value={term} 
                            onChange={setTerm} 
                            style={{ width: 150, fontWeight: 600 }}
                            size="large"
                        >
                            <Option value="Term 1">Term 1</Option>
                            <Option value="Term 2">Term 2</Option>
                            <Option value="Term 3">Term 3</Option>
                        </Select>
                    </div>
                </div>

                {/* Stats Cards */}
                <Row gutter={[24, 24]}>
                    <Col xs={24} sm={8}>
                        <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <Statistic 
                                title={<Text type="secondary" strong>Total Enrolled Students</Text>}
                                value={stats.total} 
                                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <Statistic 
                                title={<Text style={{ color: '#52c41a' }} strong>Feedback Submitted</Text>}
                                value={stats.submitted} 
                                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                suffix={`/ ${stats.total}`}
                            />
                            <Progress percent={stats.percentage} status="active" strokeColor="#52c41a" showInfo={false} style={{ marginTop: 8 }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <Statistic 
                                title={<Text style={{ color: '#faad14' }} strong>Feedback Pending</Text>}
                                value={stats.pending} 
                                prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                                suffix={`/ ${stats.total}`}
                            />
                            <Progress percent={100 - stats.percentage} status="normal" strokeColor="#faad14" showInfo={false} style={{ marginTop: 8 }} />
                        </Card>
                    </Col>
                </Row>

                {/* Filters Board */}
                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }} bodyStyle={{ padding: '18px 24px' }}>
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={8}>
                            <Input 
                                placeholder="Search by student name, ID, or tutor name..." 
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col xs={12} md={5}>
                            <Select 
                                placeholder="Filter by Class" 
                                value={selectedClass} 
                                onChange={setSelectedClass}
                                style={{ width: '100%' }}
                            >
                                <Option value="All">All Classes</Option>
                                {classes.map(c => (
                                    <Option key={c.id} value={c.name}>{c.name}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={12} md={5}>
                            <Select 
                                placeholder="Filter by Status" 
                                value={statusFilter} 
                                onChange={setStatusFilter}
                                style={{ width: '100%' }}
                            >
                                <Option value="All">All Statuses</Option>
                                <Option value="submitted">Submitted Only</Option>
                                <Option value="pending">Pending Only</Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                            <Badge status="processing" text={<Text type="secondary">Showing {filteredStudents.length} entries</Text>} />
                        </Col>
                    </Row>
                </Card>

                {/* Main Table */}
                <Table 
                    columns={columns} 
                    dataSource={filteredStudents}
                    loading={loading}
                    rowKey="student_id"
                    pagination={{ 
                        pageSize: 15,
                        showSizeChanger: true,
                        pageSizeOptions: ['15', '30', '50', '100'],
                        showTotal: (total) => `Total ${total} students`
                    }}
                    style={{ 
                        background: '#ffffff', 
                        borderRadius: '12px', 
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        overflow: 'hidden'
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </Space>

            {/* Modal to view full feedback */}
            <Modal
                title={
                    <Space>
                        <MessageOutlined style={{ color: '#1890ff' }} />
                        <span>Tutor Feedback Details</span>
                    </Space>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setModalVisible(false)}>
                        Close
                    </Button>
                ]}
                destroyOnClose
            >
                {selectedFeedback && (
                    <div style={{ marginTop: '16px' }}>
                        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                            <div>
                                <Text type="secondary">Student: </Text>
                                <Text strong style={{ fontSize: '15px' }}>{selectedFeedback.student_name}</Text>
                                <Text type="secondary"> ({selectedFeedback.class_name})</Text>
                            </div>
                            <Tag color="blue">{term}</Tag>
                        </div>
                        
                        <div style={{ marginBottom: '16px' }}>
                            <Text type="secondary" display="block" style={{ marginBottom: 4 }}>Tutor Response:</Text>
                            <Card style={{ background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                                <Paragraph style={{ margin: 0, fontSize: '15px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                    {selectedFeedback.feedback}
                                </Paragraph>
                            </Card>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#8c8c8c', fontSize: '12px' }}>
                            <div>
                                <UserOutlined /> Tutor: <Text strong>{selectedFeedback.tutor_name}</Text>
                            </div>
                            <div>
                                <CalendarOutlined /> Submitted: {dayjs(selectedFeedback.feedback_date).format('DD MMM YYYY HH:mm')}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default TutorFeedbackDashboard;
